import { useContext, useSyncExternalStore } from "react";
import type { HubConnection } from "@microsoft/signalr";
import {
    RealtimeConnectionContext,
    RealtimeStateStoreContext,
    type RealtimeConnectionState,
    type RealtimeStateStore,
} from "./realtimeContext";

/**
 * Returns the realtime connection's current UI-facing state and re-renders
 * the caller on every change. Subscribe here only where the state is
 * rendered (the status chip). Hooks that merely need to react to
 * "connected" should use `useRealtimeStateStore` and subscribe
 * imperatively, which costs no render.
 *
 * Outside a `RealtimeProvider` (e.g. an isolated component test), this
 * resolves to `"disconnected"` via the context default — no provider, no
 * connection, no fake "Live" badge.
 */
export function useConnectionState(): RealtimeConnectionState {
    const store = useContext(RealtimeStateStoreContext);
    return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

/** The shared hub connection, or `null` outside a provider / before start. */
export function useRealtimeConnection(): HubConnection | null {
    return useContext(RealtimeConnectionContext);
}

/** The state store: read `getSnapshot()` at call time, `subscribe` for change. */
export function useRealtimeStateStore(): RealtimeStateStore {
    return useContext(RealtimeStateStoreContext);
}
